import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'

// DS-017 brand fonts — Inter Variable (Latin) + Readex Pro Variable (Arabic + Latin
// fallback, readability-first brief) + JetBrains Mono Variable (mono).
// Locked 2026-05-16. Canonical CSS source for non-React consumers:
//   https://signal-ds.vercel.app/fonts/scrips-fonts.css
// Rollback to IBM Plex Sans Arabic = swap `@fontsource-variable/readex-pro` →
// `@fontsource-variable/ibm-plex-sans-arabic` here + in package.json + update
// index.css `--font-sans` stack. See DESIGN-STATE.md DS-017 §Rollback.
import '@fontsource-variable/inter'
import '@fontsource-variable/readex-pro'
import '@fontsource-variable/jetbrains-mono'

import '@scripsteam/scrips-signal-ds/tokens.css'
// DS component classes are generated by this app's own Tailwind v4 via the
// @source directive in index.css (it scans the DS dist). We do NOT import the
// DS's pre-built styles.css — its self-contained utilities layer collides with
// the app's and zeroes padding utilities (px-*) across the surface.
import './index.css'
// Showcase look: the designer's contract, then the bridge onto the DS token names. Both are
// unlayered and must come after index.css, contract first.
import './styles/contract.css'
import './styles/showcase-bridge.css'
import { AppRouter } from './app/routes'
import { queryClient } from './app/providers/query-client'
import { resyncUserOnBoot } from './auth/auth-store'
// Must run before render so a crash during the first paint is still reported.
// No-ops unless VITE_SENTRY_DSN is set — and because Vite substitutes env at
// build time, a build without one tree-shakes the SDK out entirely.
import { initObservability } from './observability/sentry'

initObservability()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <Toaster position="bottom-center" richColors />
      <AppRouter />
    </QueryClientProvider>
  </StrictMode>,
)

// One-time cold-boot profile re-sync (CheckUserDataToken). Called explicitly
// here rather than as an auth-store module-init side effect, so importing the
// store (e.g. in unit tests) never fires this fetch. Fire-and-forget — failures
// are swallowed inside and never block app startup.
void resyncUserOnBoot()
